import React from "react";

import Select from "./Select";

const ChevronLeft = (props) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...props}
    >
        <polyline points="15 18 9 12 15 6" />
    </svg>
);

const ChevronRight = (props) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...props}
    >
        <polyline points="9 18 15 12 9 6" />
    </svg>
);

/**
 * Sentinel value for "All" rows-per-page option (no pagination).
 * Consumers can include this in `rowsPerPageOptions` to expose an
 * "All" choice that disables paging.
 */
export const ALL_ROWS = -1;

/**
 * Server-driven pagination control.
 *
 * @param {number}   page              0-indexed current page
 * @param {number}   rowsPerPage       per-page count, or `ALL_ROWS` (-1) for "All"
 * @param {number}   count             total row count across all pages
 * @param {Array<number | {value: number, label: string}>} rowsPerPageOptions
 * @param {Function} onPageChange      (newPage)     => void
 * @param {Function} onRowsPerPageChange (newRows)  => void
 */
const Pagination = ({
    page,
    rowsPerPage,
    count,
    rowsPerPageOptions = [25, 50, 100],
    onPageChange,
    onRowsPerPageChange,
    className = "",
}) => {
    const normalizedOptions = rowsPerPageOptions.map((opt) =>
        typeof opt === "number"
            ? { value: opt, label: opt === ALL_ROWS ? "All" : String(opt) }
            : opt,
    );
    const showingAll = rowsPerPage === ALL_ROWS;
    const totalPages = showingAll ? 1 : Math.max(1, Math.ceil(count / rowsPerPage));
    const start = count === 0 ? 0 : showingAll ? 1 : page * rowsPerPage + 1;
    const end = showingAll ? count : Math.min(count, (page + 1) * rowsPerPage);
    const canPrev = !showingAll && page > 0;
    const canNext = !showingAll && page + 1 < totalPages;

    return (
        <div
            className={`flex flex-col items-stretch gap-3 border-t border-ink-100 px-4 py-3 text-sm text-ink-600 sm:flex-row sm:items-center sm:justify-between sm:px-6 ${className}`}
        >
            <div className="flex items-center gap-2">
                <span className="text-ink-500">Rows per page</span>
                <div className="w-20">
                    <Select
                        value={rowsPerPage}
                        onChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
                        aria-label="Rows per page"
                    >
                        {normalizedOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </Select>
                </div>
            </div>

            <div className="flex items-center justify-between gap-4 sm:justify-end">
                <span className="tabular-nums text-ink-500">
                    {count === 0 ? "0" : `${start}–${end}`} of{" "}
                    <span className="font-medium text-ink-700">{count}</span>
                </span>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => onPageChange(page - 1)}
                        disabled={!canPrev}
                        aria-label="Previous page"
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-600 transition-colors hover:bg-ink-100 disabled:cursor-not-allowed disabled:text-ink-300 disabled:hover:bg-transparent"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => onPageChange(page + 1)}
                        disabled={!canNext}
                        aria-label="Next page"
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-600 transition-colors hover:bg-ink-100 disabled:cursor-not-allowed disabled:text-ink-300 disabled:hover:bg-transparent"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Pagination;
